/**
 * EmptyState — the shared "nothing to show here" block for cold surfaces.
 *
 * Carbon order: headline → body → optional CTA (DESIGN.md "The No-Kicker
 * Rule": a block opens with its headline). Callers pass copy; this
 * component owns the layout only.
 *
 * Copy rules (CLAUDE.md § Product Voice): the headline is one sentence that
 * names the subject and the next action ("Add a tenant to begin
 * monitoring."), the body adds one fact the reader would otherwise lack. No
 * exclamation marks, no "Please …", no em-dashes. The all-clear is
 * "Nothing to review."
 */

import type { ComponentType, SVGProps } from 'react'
import clsx from 'clsx'
import { ButtonLink } from '@/components/carbon/Button'
import { IconArrowRight } from '@/components/carbon/icons'

export interface EmptyStateProps {
  /** One sentence; names the subject and the next action. */
  headline: string
  /** Optional one- or two-sentence fact. */
  body?: string
  /** Optional in-app CTA, rendered as a tertiary button link. */
  cta?: { label: string; to: string }
  /** Optional heroicon (outline), drawn at 32px in the secondary icon tone. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>
  /** Centre the block (tenant-picker cards) instead of left-aligning it. */
  align?: 'start' | 'center'
  /** Extra classes for the outer wrapper (padding lives with the caller's card). */
  className?: string
  /** The headline's element. Default h4 (a block inside a section); h1 when the empty state is the whole view
   *  (no tenant, no sync yet), h2 or h3 under the page's own headings. Same look at every level. */
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4'
}

export default function EmptyState({
  headline,
  body,
  cta,
  icon: Icon,
  align = 'start',
  className = '',
  headingLevel: Heading = 'h4',
}: EmptyStateProps) {
  const centred = align === 'center'
  return (
    <div
      data-align={align}
      className={clsx('flex flex-col justify-center py-8', centred ? 'items-center text-center' : 'items-start', className)}
    >
      {Icon && (
        <Icon
          aria-hidden="true"
          strokeLinecap="square"
          strokeLinejoin="miter"
          className="mb-4 h-8 w-8 text-[color:var(--icon-secondary)]"
        />
      )}
      <Heading className="m-0 max-w-[32ch] text-heading font-normal text-text-primary">{headline}</Heading>
      {body && <p className="m-0 mt-2 max-w-[48ch] text-body text-text-secondary">{body}</p>}
      {cta && (
        <ButtonLink to={cta.to} kind="tertiary" size="md" icon={<IconArrowRight />} className="mt-6">
          {cta.label}
        </ButtonLink>
      )}
    </div>
  )
}
